/**
 * Landing-page campaign params, held for the session and forwarded to the API.
 *
 * Why this exists: the API derives its `utm` column from the request URL it
 * receives (`/waitlist`, `/bookings`), which never carries campaign params, so
 * every server-mirrored analytics event reached PostHog with no `utm_*`. Person
 * -level `$initial_utm_*` still worked (posthog-js sets it in the browser), but
 * Marketing Analytics conversion goals read `utm_source` / `utm_campaign` off
 * the EVENT, so paid channels showed 0 conversions against real spend.
 *
 * Session-scoped on purpose. A campaign visit is a session: `sessionStorage`
 * gives last-touch semantics matching the project's `attribution_mode`, and
 * keeps an ad click from labelling an organic visit weeks later. The long-window
 * story is already covered by the person's `$initial_utm_*`, which posthog-js
 * persists independently of this.
 *
 * Deliberately does NOT read posthog-js persistence. It stores
 * `$initial_campaign_params` as an internal object and derives `$initial_utm_*`
 * at capture time, so there is no public key to read and the shape is free to
 * change between minor versions.
 */

const KEY = "ezspot_landing_utm";

/** The canonical set, matching UTM_KEYS in the API's analytics-context.ts. */
const UTM_KEYS = [
  "utm_source",
  "utm_medium",
  "utm_campaign",
  "utm_term",
  "utm_content",
] as const;

/**
 * Header cap. The API drops anything longer rather than truncating a value into
 * a different campaign; keep the two in sync.
 */
const MAX_LENGTH = 512;

/** The utm_* params present in `params`, as a query string, or null if none. */
function serialize(params: URLSearchParams | null): string | null {
  if (!params) return null;
  const utm = new URLSearchParams();
  for (const key of UTM_KEYS) {
    const value = params.get(key);
    if (value) utm.set(key, value);
  }
  const serialized = utm.toString();
  if (!serialized || serialized.length > MAX_LENGTH) return null;
  return serialized;
}

/**
 * The query buried in an auth redirect's `next`, or null if it carries none.
 *
 * `src/proxy.ts` sends an unauthenticated hit on a protected route to
 * `/auth?next=<path><search>` and clears the top-level query as it goes, so a
 * paid click landing on `/reserve/:id` reaches the signup form with its
 * campaign params one level down. Signup is the conversion that most needs
 * labelling, and it fires from `/auth` before the post-auth redirect restores
 * the original URL — without this, that click is attributed to nothing.
 *
 * Split on the first `?` rather than parsing with `new URL()`: URL treats `#`
 * as a fragment delimiter, so a `next` carrying one would silently drop every
 * param after it and name a different campaign.
 */
function nestedParams(next: string | null): URLSearchParams | null {
  if (!next) return null;
  const start = next.indexOf("?");
  if (start === -1) return null;
  return new URLSearchParams(next.slice(start + 1));
}

/**
 * Store this navigation's campaign params, if it has any. A navigation without
 * them leaves the stored value alone — the visitor is moving through the site,
 * not arriving from somewhere new.
 */
export function captureLandingUtm(params: URLSearchParams) {
  if (typeof window === "undefined") return;
  // Top level wins; `next` is only consulted when the navigation itself carries
  // no campaign, which is the shape the proxy's auth redirect produces.
  const serialized =
    serialize(params) ?? serialize(nestedParams(params.get("next")));
  if (!serialized) return;
  try {
    sessionStorage.setItem(KEY, serialized);
  } catch {
    // sessionStorage may be unavailable (private mode, quota exceeded)
  }
}

/** The session's landing campaign params as a query string, or null. */
export function getStoredLandingUtm(): string | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw || raw.length > MAX_LENGTH) return null;
    return raw;
  } catch {
    return null;
  }
}
